import { message } from "antd";
import AppApi from "api/App/AppApi";
import { GetConfigurationModel } from "api/App/model/GetConfigurationModel";
import AuthenticationApi from "api/authentication/AuthenticationApi";
import {
  AuthDataModel,
  UserProfileModel,
} from "api/authentication/model/LoginResponseModel";
import AppConfigResponseModel, {
  MenuItem,
} from "api/config/model/AppConfigResponseModel";
import ConfigRepository from "api/config/model/ConfigRepository";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import ModelConverter from "utils/format/ModelConverter";
import { isPaginatedData } from "utils/typeGuards/typeGuards";

export const AuthContext = createContext({});

const LOCAL_PROFILE = "LP";
const LOCAL_ACCESS_TOKEN = "LA";
const MENU = "MENU";

const allowAnonymous = ["/sign-in", "/forgot-password"];

export const AuthProvider = ({ children }: any) => {
  const navigate = useNavigate();

  const [notiLoading, setNotiLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<any>(false);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [listNoti, setListNoti] = useState<any>([]);
  const [setting, setSetting] = useState<any>([]);
  const [fee, setFree] = useState<any>({
    simActivation: 0,
    esimActivation: 0,
  });

  const [profile, setProfile] = useState<UserProfileModel | undefined>(
    undefined,
  );

  const [config, setConfig] = useState<{ menu: MenuItem[] } | undefined>(
    undefined,
  );

  const isAdmin = useMemo(() => {
    const defaultRole = profile?.default_role || "";
    return ["SUPER_ADMIN", "MODERATOR"].includes(defaultRole);
  }, [profile?.default_role]);

  const isAgency = useMemo(() => {
    const defaultRole = profile?.default_role || "";
    return ["AGENCY"].includes(defaultRole);
  }, [profile?.default_role]);

  const isAgencyAdmin = useMemo(() => {
    const defaultRole = profile?.default_role || "";
    return ["AGENCY_ADMIN"].includes(defaultRole);
  }, [profile?.default_role]);

  const isStaff = useMemo(() => {
    const defaultRole = profile?.default_role || "";
    return ["STAFF"].includes(defaultRole);
  }, [profile?.default_role]);

  const onLogout = useCallback(() => {
    localStorage.clear();

    setToken(undefined);
    setProfile(undefined);
    setConfig(undefined);

    navigate("/sign-in");
    setIsAuthenticated(false);
  }, [navigate]);

  const onLogged = async (model: AuthDataModel) => {
    await processUserProfileData(model);
    navigate("/dashboard");
    if (model?.user?.default_role === "AGENCY") {
      navigate("/order/dkttb");
    } else {
      navigate("/dashboard");
    }
  };

  const getAppConfig = async (userProfile: AuthDataModel) => {
    try {
      let response: any = await ConfigRepository.getAppConfig(
        userProfile?.user?.default_role,
      );

      if (
        response?.data?.raw &&
        Array.isArray(response?.data?.raw?.menu) &&
        response.data?.raw?.menu?.length > 0
      ) {
        setConfig(response.data?.raw);

        const dataString = JSON.stringify(response?.data?.raw);
        localStorage.setItem(MENU, dataString);
      } else {
        message.error(
          response?.error?.message ||
            "Có lỗi xảy ra khi lấy cấu hình hệ thống. Vui lòng đăng nhập lại",
        );
        setTimeout(() => {
          onLogout();
        }, 2000);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      message.error("Có lỗi xảy ra khi lấy thông tin menu");
    }
  };

  const processUserProfileData = async (model?: AuthDataModel) => {
    const accessToken = model?.access_token || "";

    if (!!accessToken) {
      localStorage.setItem(LOCAL_ACCESS_TOKEN, accessToken);
      setToken(accessToken);
    }

    const userProfile = model?.user;

    localStorage.setItem(
      LOCAL_PROFILE,
      JSON.stringify(ModelConverter.encode(model)),
    );

    getAppConfig(model!);

    setProfile(userProfile);

    setIsAuthenticated(true);

    getUserInfo();
  };

  const getUserInfo = async () => {
    try {
      await handleNoti();
      const response = await AppApi.getProfile();

      const profileData = localStorage.getItem(LOCAL_PROFILE);
      const parsedProfile = JSON.parse(profileData!);

      if (response?.code === 0) {
        const data = response?.data?.raw;

        const detail = {
          ...parsedProfile,
          user: {
            ...parsedProfile?.user,
            username: data?.username,
            email: data?.email,
            phone_number: data?.phoneNumber,
            full_name: data?.fullName,
            address: data?.address,
            wallet: {
              ...parsedProfile?.user?.wallet,
              balance: data?.balance,
              limit_amount: data?.limitAmount,
              frozen_amount: data?.frozenAmount,
              revenue: data?.revenue,
              commission: data?.commission,
              totalTransaction: data?.totalTransaction,
            },
          },
        };

        localStorage.setItem(
          LOCAL_PROFILE,
          JSON.stringify(ModelConverter.encode(detail)),
        );

        setProfile(detail.user);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleNoti = async () => {
    try {
      setNotiLoading(true);

      const info = {
        page: 0,
        limit: 100,
      };
      const response = await AppApi.getNotiUser(info);

      if (response?.data && isPaginatedData(response.data)) {
        setListNoti(response.data.items);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setNotiLoading(false);
    }
  };

  const handleSetting = async () => {
    try {
      const response = await AppApi.getSetting();

      if (response?.code === 0) {
        setSetting(response.data);
        if (response.data.config) {
          setFree({
            simActivation: response.data.config.ACTIVATION_FEE.value,
            esimActivation: response.data.config.ESIM_ACTIVATION_FEE.value,
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleChangePass = async (info: any) => {
    try {
      const response = await AuthenticationApi.changePassword(info);

      if (response?.code !== 0) {
        message.error(response?.error?.message || "Có lỗi xảy ra");
      } else {
        message.success("Đổi mật khẩu thành công");
        onLogout();
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem(LOCAL_ACCESS_TOKEN);
    const profileData = localStorage.getItem(LOCAL_PROFILE) || "{}";

    if (!!token && !!profileData && profileData !== "undefined") {
      try {
        const parsedProfile = JSON.parse(profileData);
        handleSetting();
        processUserProfileData(parsedProfile);
      } catch (error) {
        console.error("Lỗi khi parse profileData từ localStorage:", error);
        onLogout();
      }
    } else {
      if (allowAnonymous.includes(window.location.pathname)) {
        navigate(window.location.pathname, { replace: true });
      } else {
        navigate("/sign-in", { replace: true });
      }
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        onLogged,
        onLogout,
        profile,
        setting,
        config,
        token,
        isAdmin,
        isAgency,
        notiLoading,
        listNoti,
        fee,
        getUserInfo,
        handleChangePass,
        isStaff,
        isAgencyAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

interface AuthenticationProps {
  onLogged: any;
  onLogout: any;
  updateProfile: any;
  token: string;
  profile: UserProfileModel;
  config: AppConfigResponseModel;
  onSignUp: any;
  onNeedFetchUserProfile: any;
  isAuthenticated: boolean;
  loading: boolean;
  configuration: GetConfigurationModel;
  notifications: any;
  agency: any;
  isAdmin: boolean;
  isAgency: boolean;
  getUserInfo: any;
  notiLoading: boolean;
  listNoti: any;
  handleChangePass: any;
  isStaff: boolean;
  isAgencyAdmin: boolean;
  setting: any;
  fee: any;
}

export const useAuth = (): AuthenticationProps =>
  useContext(AuthContext) as AuthenticationProps;
